import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, Video, FileImage, Clock } from "lucide-react";
import { EDUCATION_LIST } from "@/data/education";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import SectionTitle from "@/components/SectionTitle";
import SEO from "@/components/SEO";

const CATS = ["All", "Cybercrime", "Human Trafficking", "AML", "Fraud", "Youth Safety", "Law"];
const TYPE_ICON = { "IOCRI Educational Guide": BookOpen };

export default function Education() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const filtered = EDUCATION_LIST.filter((a) =>
    (cat === "All" || a.category === cat) &&
    (a.title.toLowerCase().includes(q.toLowerCase()) || a.summary.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="education-page">
      <SEO
        title="Public Education"
        description="Free, source-verified educational content on organized crime, cybercrime, money laundering, human trafficking, fraud, and international law."
        path="/education"
        image="institute"
      />
      <SectionTitle
        eyebrow="PUBLIC EDUCATION"
        title="Free knowledge, at the frontline of prevention."
        subtitle="Guides and explainers by IOCRI, backed by real sources from UNODC, FATF, INTERPOL, Europol, IMF, and academic authorities."
      />

      <div className="mt-8 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search guides…"
            data-testid="education-search"
            className="pl-9 bg-black border-white/10 h-11 rounded-full text-white"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`cat-${c}`}
              className={`px-4 py-2 rounded-full text-xs font-mono tracking-widest border transition-colors whitespace-nowrap ${
                cat === c
                  ? "bg-gold text-black border-gold"
                  : "border-white/10 text-white/70 hover:border-gold/40"
              }`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((a) => {
          const I = TYPE_ICON[a.type] || BookOpen;
          return (
            <Link
              key={a.slug}
              to={`/education/${a.slug}`}
              data-testid={`edu-card-${a.slug}`}
              aria-label={`Read: ${a.title}`}
              className="block group focus:outline-none focus:ring-2 focus:ring-gold rounded-sm doc-physics"
            >
              <div className="doc-ghost doc-ghost-2 rounded-sm" aria-hidden />
              <div className="doc-ghost doc-ghost-1 rounded-sm" aria-hidden />
              <Card className="doc-layer doc-layer-front relative bg-obsidian-soft border-white/10 rounded-sm hover:border-gold/40 transition-all cursor-pointer h-full">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <span className="evidence-chip">{a.type.toUpperCase()}</span>
                    <div className="text-[10px] font-mono text-white/40 inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {a.read}
                    </div>
                  </div>
                  <I className="h-6 w-6 text-gold mt-5" />
                  <div className="font-display text-lg md:text-xl mt-3 text-white leading-tight group-hover:text-gold-light transition-colors">
                    {a.title}
                  </div>
                  <div className="mt-3 text-white/60 text-sm">{a.summary}</div>
                  <div className="mt-4 text-xs text-white/50">{a.category}</div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Download Center — honest state */}
      <div className="mt-16">
        <div className="text-gold font-mono text-xs tracking-widest">DOWNLOAD CENTER</div>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          {[
            "Scam-Prevention Toolkit",
            "AML Educator Bundle",
            "School Safety Curriculum",
          ].map((n) => (
            <div
              key={n}
              className="rounded-2xl border border-white/5 bg-obsidian-soft p-5 flex items-center justify-between opacity-80"
              data-testid={`dl-${n.split(" ")[0].toLowerCase()}`}
            >
              <div>
                <div className="text-xs font-mono text-gold">RESOURCE</div>
                <div className="font-display text-lg mt-1">{n}</div>
                <div className="text-white/50 text-xs mt-1 italic">Resource in development</div>
              </div>
              <div className="text-[10px] font-mono text-white/40 border border-white/10 rounded-full px-3 py-1">
                COMING SOON
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
